
const ajaxCart = class extends HTMLElement {
  constructor () {
    super()
    this.open = false
    this.itemsInCart = null
    this.cartSignature = null
    this.drawerPopup = this.closest('popup-element')
    this.initToggles()

    const activePopupId = window.localStorage.getItem('activePopup')

    if (Shopify.designMode && activePopupId === 'ajaxcart') {
      cartAPI.cart()
    }
  }

  initToggles () {
    window.addEventListener('cartAPI', ({ detail: { cartData } }) => {
      if (this.itemsInCart !== null && this.itemsInCart < cartData.item_count) {
        this.openAjaxcart()
      }

      this.itemsInCart = cartData.item_count
      this.freeGiftBar(cartData)
      this.refreshUpsells(cartData)
    })

    if (window.location.href.split('?')[1]?.includes('cart_redirect')) {
      this.openAjaxcart()
    }
  }

  // Fast Simon widgets re-request recommendations when they hear their own add-to-cart event.
  // Skip the initial page-load cart pull and any refresh where the cart contents didn't change.
  refreshUpsells (cartData) {
    const cartSignature = cartData.items.map(item => `${item.key}:${item.quantity}`).join('|')

    if (this.cartSignature !== null && this.cartSignature !== cartSignature && cartData.item_count > 0) {
      document.dispatchEvent(new CustomEvent('fs-custom-events-add-to-cart-response', { detail: { cart: {} } }))
    }

    this.cartSignature = cartSignature
  }

  openAjaxcart () {
    this.drawerPopup.open = true
  }

  freeGiftBar (cartData) {
    const fsb = this.querySelector('[data-fsb]')
    if (!fsb) { return }

    const threshold = parseInt(fsb.getAttribute('data-fsb'), 10) * 100
    const bar = this.querySelector('[data-fsb-bar]')
    const text = this.querySelector('[data-fsb-text]')

    if (bar !== null) {
      const barPercent = (cartData.total_price / threshold) * 100
      bar.style.width = `${barPercent > 100 ? 100 : barPercent}%`
    }

    if (text !== null) {
      text.innerHTML = cartData.total_price >= threshold
        ? text.getAttribute('data-qualified-text')
        : text.getAttribute('data-unqualified-text')
          .replace(/\[amount\]/g, Shopify.formatMoney(threshold - cartData.total_price))
    }
  }
}

if (typeof window.customElements.get('ajax-cart') === 'undefined') {
  window.customElements.define('ajax-cart', ajaxCart)
}

function isAjaxcartUpsellCard (productElement) {
  if (!productElement) { return false }
  if (productElement.closest('.ajaxcart__upsells')) { return true }

  const flyoutWidget = document.querySelector('.ajaxcart__upsells [class*="fast-simon-new-upsell-"]')
  const widgetIdClass = flyoutWidget
    ? [...flyoutWidget.classList].find((name) => /^fast-simon-new-upsell-\d+$/.test(name))
    : null

  return Boolean(widgetIdClass && productElement.closest(`.${widgetIdClass}`))
}

const shopifyProductCache = new Map()

function getHandleFromUrl (url) {
  const match = String(url || '').match(/\/products\/([^/?#]+)/)
  return match ? decodeURIComponent(match[1]) : null
}

function getProductHandle (data) {
  return data?.handle || getHandleFromUrl(data?.productURL || data?.u)
}

function getShopifyProduct (handle) {
  if (!handle) { return Promise.resolve(null) }
  if (shopifyProductCache.has(handle)) { return shopifyProductCache.get(handle) }

  const request = fetch(`/products/${handle}.js`)
    .then((response) => response.ok ? response.json() : null)
    .catch(() => null)

  shopifyProductCache.set(handle, request)
  return request
}

function getVariantIds (data) {
  return (data?.variants || [])
    .map((variant) => Number(variant.id || variant))
    .filter(Boolean)
}

function getVariantLabel (variant, product) {
  const sizeOptionIndex = (product?.options || []).findIndex((option) => /size/i.test(option.name))
  if (sizeOptionIndex > -1) {
    return variant[`option${sizeOptionIndex + 1}`] || variant.title
  }

  return variant.public_title && variant.public_title !== 'Default Title'
    ? variant.public_title
    : variant.title
}

// Reduce a Shopify image URL to its filename without size suffix, so card and variant images compare.
function getImageKey (src) {
  const file = String(src || '').split('?')[0].split('/').pop()
  return file.replace(/_(?:\d+x\d*|\d*x\d+)(?:_crop_[a-z]+)?(?:@\dx)?(?=\.[a-z0-9]+$)/i, '').toLowerCase()
}

// Variants can share a SKU across colors, so narrow by the card's image, then the color in its title.
function getCardVariant (product, productElement, colorKey) {
  const sku = productElement.dataset.skuId
  const matches = sku ? (product?.variants || []).filter((variant) => variant.sku === sku) : []
  if (matches.length < 2 || !colorKey) { return matches[0] || null }
  if (matches.every((variant) => variant[colorKey] === matches[0][colorKey])) { return matches[0] }

  const imageKey = getImageKey(productElement.querySelector('img')?.getAttribute('src'))
  const byImage = imageKey && matches.find((variant) => getImageKey(variant.featured_image?.src) === imageKey)
  if (byImage) { return byImage }

  const title = (productElement.querySelector('[class*="title"]')?.textContent || '').toLowerCase()
  const byTitle = matches.filter((variant) => title.includes(String(variant[colorKey] || '').toLowerCase()))
  return new Set(byTitle.map((variant) => variant[colorKey])).size === 1 ? byTitle[0] : null
}

// Split-by-color cards show one color of a product; keep only that color's variants.
function getColorVariants (product, productElement) {
  const colorOptionIndex = (product?.options || []).findIndex((option) => /colou?r/i.test(option.name))
  const colorKey = colorOptionIndex > -1 ? `option${colorOptionIndex + 1}` : null
  const cardVariant = getCardVariant(product, productElement, colorKey)
  if (!cardVariant) { return [] }
  if (!colorKey) { return product.variants }

  return product.variants.filter((variant) => variant[colorKey] === cardVariant[colorKey])
}

async function getCardVariants (data, productElement) {
  const product = await getShopifyProduct(getProductHandle(data))
  const shopifyVariants = product?.variants || []

  // data-sku-id is the SKU of the variant the card is showing.
  const colorVariants = getColorVariants(product, productElement)
  if (colorVariants.length) { return { product, variants: colorVariants } }

  const cardIds = getVariantIds(data)

  if (shopifyVariants.length && cardIds.length) {
    const matched = shopifyVariants.filter((variant) => cardIds.includes(Number(variant.id)))
    if (matched.length) { return { product, variants: matched } }
  }

  if (shopifyVariants.length) { return { product, variants: shopifyVariants } }
  return cardIds.length ? { product: data, variants: data.variants } : { product: null, variants: [] }
}

function closeUpsellSelector (control) {
  if (!control) { return }
  control.classList.remove('is-open')
  control.querySelector('.fs-upsell-atc__button')?.setAttribute('aria-expanded', 'false')
  const selector = control.querySelector('[data-upsell-atc-selector]')
  if (selector) { selector.hidden = true }
}

function closeUpsellSelectors (except) {
  document.querySelectorAll('.fs-upsell-atc.is-open').forEach((control) => {
    if (control !== except) { closeUpsellSelector(control) }
  })
}

function setAtcBusy (control, isBusy) {
  control.classList.toggle('is-loading', isBusy)
  control.querySelectorAll('button').forEach((button) => {
    button.disabled = isBusy
  })
}

async function addUpsellVariant (control, variantId, product) {
  if (!variantId || typeof addVariantToCart !== 'function') { return }

  setAtcBusy(control, true)
  closeUpsellSelector(control)

  const result = await addVariantToCart(variantId, product)
  setAtcBusy(control, false)

  if (result instanceof Error) {
    const error = control.querySelector('[data-upsell-atc-error]')
    if (!error) { return }
    error.textContent = result.message || 'Unable to add this item.'
    error.hidden = false
    return
  }

  document.querySelector('.ajaxcart__items-wrapper')?.scrollTo({ top: 0, behavior: 'smooth' })
}

function renderVariantOptions (selector, variants, product) {
  selector.replaceChildren()

  // Size-only labels repeat when the color couldn't be narrowed down, so show the full title then.
  const labels = variants.map((variant) => getVariantLabel(variant, product))
  const hasDuplicateLabels = new Set(labels).size < labels.length

  variants.forEach((variant, index) => {
    const option = document.createElement('button')
    option.type = 'button'
    option.className = 'fs-upsell-atc__option'
    option.textContent = hasDuplicateLabels ? (variant.public_title || variant.title) : labels[index]
    option.dataset.variantId = String(variant.id)

    if (variant.available === false) {
      option.disabled = true
      option.classList.add('is-unavailable')
    }

    selector.append(option)
  })
}

// Fast Simon rewrites data-id / data-sku-id when it reuses a card for another product.
function getCardKey (productElement) {
  return `${productElement.dataset.id || ''}:${productElement.dataset.skuId || ''}`
}

function initAjaxcartUpsellAtc (productElement, data) {
  if (!productElement) { return }
  if (!isAjaxcartUpsellCard(productElement)) { return }

  // Fast Simon reuses card nodes when recommendations refresh, so a control left over from the
  // previous product has to go before anything else, even if this product can't be resolved.
  const cardKey = getCardKey(productElement)
  const existingControl = productElement.querySelector('.fs-upsell-atc')
  if (existingControl) {
    if (existingControl.dataset.cardKey === cardKey) { return }
    existingControl.remove()
  }

  // Never build a control from another card's data.
  if (!data || String(data.id) !== String(productElement.dataset.id)) { return }
  if (!getProductHandle(data)) { return }

  const imageWrap = productElement.querySelector('.image-wrapper, .upsell-cross-sell-image-container')
  if (!imageWrap) { return }

  const title = data?.l || data?.title || 'product'
  const control = document.createElement('div')
  control.className = 'fs-upsell-atc'
  control.dataset.cardKey = cardKey
  control.innerHTML = `
    <button type="button" class="fs-upsell-atc__button" aria-label="Add ${title} to cart" aria-expanded="false" aria-haspopup="listbox">
      ${typeof fsBagIcon !== 'undefined' ? fsBagIcon : ''}
    </button>
    <div class="fs-upsell-atc__selector" data-upsell-atc-selector hidden></div>
    <p class="fs-upsell-atc__error" data-upsell-atc-error hidden></p>
  `

  imageWrap.classList.add('fs-upsell-atc-host')
  productElement.classList.add('fs-upsell-atc-card')
  imageWrap.after(control)

  const button = control.querySelector('.fs-upsell-atc__button')
  const selector = control.querySelector('[data-upsell-atc-selector]')
  const variantsRequest = getCardVariants(data, productElement)
  // Never add for a product the card has since been swapped away from.
  const isCurrentCard = () => control.dataset.cardKey === getCardKey(productElement)

  const stopCardNav = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  button.addEventListener('pointerdown', stopCardNav)
  selector.addEventListener('pointerdown', stopCardNav)

  button.addEventListener('click', async (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (control.classList.contains('is-loading') || !isCurrentCard()) { return }

    const error = control.querySelector('[data-upsell-atc-error]')
    if (error) {
      error.textContent = ''
      error.hidden = true
    }

    const { product, variants } = await variantsRequest
    if (!variants.length) { return }

    if (variants.length === 1) {
      if (variants[0].available === false) { return }
      addUpsellVariant(control, variants[0].id, product)
      return
    }

    if (!selector.childElementCount) {
      renderVariantOptions(selector, variants, product)
    }

    const willOpen = !control.classList.contains('is-open')
    closeUpsellSelectors(control)
    control.classList.toggle('is-open', willOpen)
    selector.hidden = !willOpen
    button.setAttribute('aria-expanded', String(willOpen))
  })

  selector.addEventListener('click', (event) => {
    const option = event.target.closest('[data-variant-id]')
    if (!option || option.disabled) { return }

    event.preventDefault()
    event.stopPropagation()
    closeUpsellSelector(control)
    if (!isCurrentCard()) { return }
    variantsRequest.then(({ product }) => {
      addUpsellVariant(control, option.dataset.variantId, product)
    })
  })
}

window.initAjaxcartUpsellAtc = initAjaxcartUpsellAtc

const upsellSkuLookupCache = new Map()

// Fast Simon doesn't pass refreshed recommendations to its render hook, so resolve the card's
// product from its SKU via predictive search and confirm it by the card's product ID.
function findUpsellProductBySku (productElement) {
  const { id, skuId } = productElement.dataset
  if (!id || !skuId) { return Promise.resolve(null) }
  if (upsellSkuLookupCache.has(id)) { return upsellSkuLookupCache.get(id) }

  const params = new URLSearchParams({
    q: skuId,
    'resources[type]': 'product',
    'resources[limit]': '10',
    'resources[options][fields]': 'variants.sku'
  })
  const request = fetch(`${window.Shopify?.routes?.root || '/'}search/suggest.json?${params}`)
    .then((response) => response.ok ? response.json() : null)
    .then((json) => json?.resources?.results?.products?.find((product) => String(product.id) === String(id)) || null)
    .catch(() => null)

  upsellSkuLookupCache.set(id, request)
  return request
}

// After an add, Fast Simon can run its render hook before it rewrites data-id on the reused
// cards, so re-sync controls whenever a card's identity changes. Unchanged cards return early.
function observeAjaxcartUpsells () {
  const upsells = document.querySelector('.ajaxcart__upsells')
  if (!upsells) { return }

  let frame = null
  const sync = () => {
    frame = null
    upsells.querySelectorAll('.fs-upsell-cross-sell-product').forEach((productElement) => {
      const cardId = productElement.dataset.id
      const data = window.fsUpsellProducts?.[cardId]
      initAjaxcartUpsellAtc(productElement, data)
      if (data || productElement.querySelector('.fs-upsell-atc')) { return }

      findUpsellProductBySku(productElement).then((product) => {
        if (product && productElement.dataset.id === cardId) {
          initAjaxcartUpsellAtc(productElement, product)
        }
      })
    })
  }

  new MutationObserver(() => {
    if (frame === null) { frame = window.requestAnimationFrame(sync) }
  }).observe(upsells, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-id', 'data-sku-id'] })
}

observeAjaxcartUpsells()

document.addEventListener('click', (event) => {
  if (!event.target.closest('.fs-upsell-atc')) {
    closeUpsellSelectors()
  }
})
